import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";

const guideLinks = [
  { label: "Complete Spreadsheet Guide", to: "/cssbuy-spreadsheet-guide", icon: "ri-book-open-line" },
  { label: "Smart Buying Guide", to: "/cssbuy-spreadsheet-buying-guide", icon: "ri-shopping-cart-2-line" },
  { label: "Shipping & Logistics", to: "/cssbuy-spreadsheet-shipping-guide", icon: "ri-ship-line" },
  { label: "QC & Quality Control", to: "/cssbuy-spreadsheet-qc-guide", icon: "ri-search-eye-line" },
  { label: "Best Brands & Finds", to: "/cssbuy-spreadsheet-brands", icon: "ri-award-line" },
  { label: "Safety & Scam Prevention", to: "/cssbuy-spreadsheet-safety", icon: "ri-shield-check-line" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const isHome = location.pathname === "/";

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || menuOpen
            ? "bg-background-50/95 backdrop-blur-md shadow-sm"
            : "bg-transparent"
        }`}
      >
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <Link
              to="/"
              className="flex items-center gap-2 text-foreground-900 hover:text-primary-500 transition-colors duration-200"
            >
              <span className="font-heading text-xl font-semibold tracking-tight">
                CSS<span className="text-primary-500">Spreadsheet</span>
              </span>
            </Link>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-6">
              <Link
                to="/"
                className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                  isHome
                    ? "text-primary-500"
                    : "text-foreground-700 hover:text-primary-500"
                }`}
              >
                Home
              </Link>
              <Link
                to="/cssbuy-spreadsheet-guide"
                className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                  location.pathname === "/cssbuy-spreadsheet-guide"
                    ? "text-primary-500"
                    : "text-foreground-700 hover:text-primary-500"
                }`}
              >
                Complete Guide
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
              >
                Main Site
              </a>
            </div>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-full text-foreground-700 hover:bg-background-100 transition-colors duration-200 cursor-pointer"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
            >
              {menuOpen ? (
                <i className="ri-close-line text-xl"></i>
              ) : (
                <i className="ri-menu-line text-xl"></i>
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile overlay */}
      <div
        className={`fixed inset-0 z-40 md:hidden transition-all duration-300 ${
          menuOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-foreground-900/40 backdrop-blur-sm"
          onClick={() => setMenuOpen(false)}
        />

        {/* Slide-in panel */}
        <div
          className={`absolute top-0 right-0 w-[85vw] max-w-sm h-full bg-background-50 shadow-lg transition-transform duration-300 ease-out ${
            menuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="h-full overflow-y-auto pt-20 pb-8 px-5">
            {/* Main Navigation */}
            <div className="mb-6">
              <p className="text-xs text-foreground-400 font-label font-semibold uppercase tracking-wider mb-3">
                Navigation
              </p>
              <div className="space-y-1">
                <Link
                  to="/"
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    isHome
                      ? "bg-primary-50 text-primary-600"
                      : "text-foreground-700 hover:bg-background-100"
                  }`}
                >
                  <i className="ri-home-4-line w-4 h-4 flex items-center justify-center"></i>
                  Home
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors duration-200"
                >
                  <i className="ri-external-link-line w-4 h-4 flex items-center justify-center"></i>
                  Main Site (kaspreadsheet.com)
                </a>
              </div>
            </div>

            {/* Spreadsheet Guides */}
            <div className="mb-6">
              <p className="text-xs text-foreground-400 font-label font-semibold uppercase tracking-wider mb-3">
                Spreadsheet Guides
              </p>
              <div className="space-y-1">
                {guideLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                      location.pathname === link.to
                        ? "bg-primary-50 text-primary-600"
                        : "text-foreground-700 hover:bg-background-100"
                    }`}
                  >
                    <i className={`${link.icon} w-4 h-4 flex items-center justify-center`}></i>
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div>
              <p className="text-xs text-foreground-400 font-label font-semibold uppercase tracking-wider mb-3">
                Quick Actions
              </p>
              <div className="space-y-2">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-primary-500 hover:bg-primary-600 text-white font-label text-sm font-semibold rounded-full transition-all duration-200"
                >
                  <i className="ri-external-link-line w-4 h-4 flex items-center justify-center"></i>
                  CSSBuy Sheet
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-secondary-500 hover:bg-secondary-600 text-white font-label text-sm font-semibold rounded-full transition-all duration-200"
                >
                  <i className="ri-shopping-bag-3-line w-4 h-4 flex items-center justify-center"></i>
                  Buy It Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}